import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import STableLayout from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import SText from "@/styles/SText";
import { QuotationRequest } from "@/types/requirement";
import { AcceptStateEnum, PurchaseOrderStateEnum } from "@/types/purchase";
import { App, Button, Flex, Modal, Progress, Spin, Tag } from "antd";
import { acceptStateUpdate } from "@/apis/supplier";
import { getQuotationPrice, numFormmater } from "@/pages/Requirement/util";

interface PurchasedQuotationsProps {
  items: Array<QuotationRequest>;
  onAcceptSuccess?: (items: Array<QuotationRequest>) => void;
}

const PurchasedQuotations: FC<PurchasedQuotationsProps> = ({ items, onAcceptSuccess }) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [processNum, setProcessNum] = useState<number>(0);

  const handleAccept = async () => {
    if (items.length === 0) {
      notification.warning({
        message: t("requirement.noData"),
        description: t("requirement.noPurchaseOrderToAccept"),
        placement: "top"
      });
      return;
    }

    setLoading(true);
    const successItems: QuotationRequest[] = [];
    const failedItems: QuotationRequest[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const orderId = item.purchaseItem?.orderId;
      if (!orderId) {
        failedItems.push(item);
        setProcessNum(i + 1);
        continue;
      }

      try {
        await acceptStateUpdate(orderId, 1);
        successItems.push({
          ...item,
          purchaseItem: {
            ...item.purchaseItem,
            order: {
              ...item.purchaseItem?.order,
              acceptState: 1
            }
          }
        } as QuotationRequest);
      } catch (error) {
        console.error(`Failed to accept order ${orderId}:`, error);
        failedItems.push(item);
      }
      setProcessNum(i + 1);
    }

    if (failedItems.length > 0) {
      notification.warning({
        message: `${t("requirement.successAccept")} ${successItems.length} ${t("requirement.purchaseOrder")},${t("requirement.acceptFailed")} ${failedItems.length} 个`,
        description: failedItems.map(item => item.purchaseItem?.number).join(", "),
        placement: "top"
      });
    } else {
      notification.success({
        message: `${t("requirement.successAccept")} ${successItems.length} ${t("requirement.purchaseOrder")}`,
        placement: "top"
      });
    }

    setLoading(false);
    if (successItems.length > 0 && onAcceptSuccess) {
      onAcceptSuccess(successItems);
    }
  };

  const columns: ColumnsType<QuotationRequest> = useMemo(() => [
    {
      title: t("requirement.purchaseOrderNumber"),
      dataIndex: ['purchaseItem', 'number'],
      key: 'orderNumber',
      width: 150,
      fixed: "left",
      render: (_, item) => <SText text={item.purchaseItem?.number ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: t("product.code"),
      dataIndex: ['purchaseItem', 'product', 'code'],
      key: 'productCode',
      width: 120,
      render: (_, item) => <SText text={item.requirementItem?.product?.code ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("product.name"),
      dataIndex: ['purchaseItem', 'product', 'name'],
      key: 'productName',
      width: 150,
      render: (_, item) => <SText text={item.requirementItem?.product?.name ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("product.model"),
      dataIndex: ['purchaseItem', 'product', 'model'],
      key: 'productModel',
      width: 120,
      render: (_, item) => <SText text={item.requirementItem?.product?.model ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("product.uom"),
      dataIndex: ['purchaseItem', 'product', 'uom'],
      key: 'uom',
      width: 100,
      render: (_, item) => <SText text={item.purchaseItem?.product?.uom ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("purchase.purPrice"),
      dataIndex: 'price',
      key: 'price',
      width: 120,
      render: (_, item) => {
        const price = getQuotationPrice(item);
        return <SText text={price === null ? "" : numFormmater(price)} style={{ width: "100%" }} />;
      },
    },
    {
      title: t("requirement.purchaseQuantity"),
      dataIndex: ['purchaseItem', 'count'],
      key: 'count',
      width: 100,
      fixed: "right",
      render: (_, item) => <SText text={(item.purchaseItem?.count ?? "") + ''} style={{ width: "100%" }} />,
    },
    {
      title: t("requirement.orderStatus"),
      dataIndex: ['purchaseItem', 'orderState'],
      key: 'orderState',
      width: 100,
      fixed: "right",
      render: (_, item) => {
        const state = item.purchaseItem?.orderState;
        if (state === PurchaseOrderStateEnum.PAYED) return <Tag color="green">{t("requirement.ordered")}</Tag>;
        if (state === PurchaseOrderStateEnum.DONE) return <Tag color="blue">{t("requirement.finished")}</Tag>;
        return <Tag>{t("requirement.notOrdered")}</Tag>;
      },
    },
    {
      title: t("requirement.acceptStatus"),
      dataIndex: ['purchaseItem', 'order', 'acceptState'],
      key: 'acceptState',
      width: 120,
      fixed: "right",
      render: (_, item) => {
        const state = item.purchaseItem?.order?.acceptState;
        if (state === null) return <Tag color="blue">{t("purchase.pend")}</Tag>;
        if (state === AcceptStateEnum.RESOLVED) return <Tag color="green">{t("requirement.accepted")}</Tag>;
        return <Tag color="red">{t("requirement.rejected")}</Tag>;
      },
    },
  ], [t]);

  // const disAccept = useMemo(() => {
  //   return items.length === 0 || !!!items.find(item => item.purchaseItem?.order.acceptState !== AcceptStateEnum.RESOLVED)
  // }, [items])

  // const headerChildren = (
  //   <Flex style={{ width: "100%" }}>
  //     <Button type="primary" disabled={disAccept} loading={loading} onClick={handleAccept}>{t("requirement.oneClickAccept")}</Button>
  //   </Flex>
  // );

  return (
    <>
      <STableLayout
        headerChildren={null}
        dataSource={items}
        columns={columns as ColumnsType<AnyObject>}
        scrollX={"max-content"}
        total={items.length}
        handleChange={() => { }}
        pagination={false}
        extHeight={70}
      />
      <Modal
        title={t('requirement.accepting') + "..."}
        closable={false}
        footer={null}
        maskClosable={false}
        open={loading}
        centered
        width={1000}
      >
        <Flex style={{ marginTop: 120, marginBottom: 100 }} justify="center">
          <Spin spinning={loading} size="large" />
        </Flex>
        <Progress percent={Number(((processNum / items.length) * 100).toFixed(2))} />
        <span>{`${t("requirement.processed")}${processNum}/${items.length}`}</span>
      </Modal>
    </>
  );
};

export default PurchasedQuotations;
